/**
 * 기록 종류 선택기 — 모든 등록 폼의 최상단에 오는 단일 진입 UI.
 *
 * 왜 필요한가: 예전에는 "일정 등록", "메모 등록", "출장 일지 추가", "파일 업로드"
 * 가 각기 다른 버튼·다른 다이얼로그였다. 현장 담당자는 남길 내용을 먼저 떠올리고
 * 그 다음에 "이건 어느 버튼이지" 를 찾아야 했다. 순서를 뒤집는다 — 하나의 등록
 * 진입점을 열고, 여기서 종류를 고른다.
 *
 * 배치: 좁은 화면에서 4개를 한 줄에 넣으면 셀 폭이 90px 밑으로 떨어져 라벨이
 * 잘린다. 그래서 xs 는 2×2, sm 이상은 1×4 격자다. 셀 자체가 큰 탭 타깃(최소
 * 64px 높이)이라 장갑 낀 손으로도 누를 수 있다.
 *
 * 순서는 사용 빈도순 — 일정 · 메모 · 파일 이 현장에서 가장 많이 쓰인다.
 */
import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import {
  DescriptionOutlined as VisitLogIcon,
  EventOutlined as ScheduleIcon,
  StickyNote2Outlined as MemoIcon,
  UploadFileOutlined as FileIcon,
} from '@mui/icons-material';

/**
 * 등록할 수 있는 기록의 종류.
 *
 * schedule/memo 는 같은 테이블(site_schedules)의 event_type 차이지만, 날짜 필수
 * 여부가 달라 사용자에게는 별개 선택지로 보여야 한다. visitlog/file 은 아예 다른
 * 엔티티라 폼 자체가 바뀐다.
 */
export type RecordKind = 'schedule' | 'memo' | 'file' | 'visitlog';

/** 격자에 놓이는 기본 순서 — 사용 빈도순. */
export const RECORD_KINDS: RecordKind[] = ['schedule', 'memo', 'file', 'visitlog'];

/** site_schedules 로 저장되는 종류 — 이 둘 사이는 폼을 유지한 채 전환된다. */
export const isScheduleKind = (k: RecordKind): boolean => k === 'schedule' || k === 'memo';

interface KindMeta {
  label: string;
  /** 셀 밑에 붙는 한 줄 — "무엇을 고르는 것인지" 를 설명한다. */
  caption: string;
  /** 선택된 뒤 폼 위에 보여줄 안내. */
  hint: string;
  Icon: typeof ScheduleIcon;
}

export const RECORD_KIND_META: Record<RecordKind, KindMeta> = {
  schedule: {
    label: '일정',
    caption: '예정된 일',
    hint: '날짜가 필요하고 캘린더에 표시됩니다.',
    Icon: ScheduleIcon,
  },
  memo: {
    label: '공지사항',
    caption: '날짜 없이',
    hint: '날짜 없이 남길 수 있습니다. 날짜를 넣으면 캘린더에도 표시됩니다.',
    Icon: MemoIcon,
  },
  file: {
    label: '파일',
    caption: '도면·사진·문서',
    hint: '여러 개를 한 번에 올릴 수 있습니다.',
    Icon: FileIcon,
  },
  visitlog: {
    label: '출장 일지',
    caption: '다녀온 기록',
    hint: '방문일·방문자·목적을 남깁니다. 방문 후 작성하는 기록입니다.',
    Icon: VisitLogIcon,
  },
};

export interface RecordKindSelectorProps {
  value: RecordKind;
  onChange: (kind: RecordKind) => void;
  /**
   * 노출할 선택지. 수정 모드처럼 종류를 바꿀 수 없는 상황에서는 좁혀서 넘긴다
   * (엔티티가 다르면 변환이 불가능하다 — 일정↔메모만 같은 테이블).
   */
  kinds?: RecordKind[];
  disabled?: boolean;
}

/**
 * 넘긴 값이 종류를 옮길 때 함께 따라가는 공통 입력값.
 *
 * 종류를 잘못 고른 걸 중간에 깨달았을 때 지금까지 쓴 내용을 다시 타이핑하게
 * 만들지 않는다. 필드 이름은 종류마다 다르지만 담는 뜻은 같다:
 *   제목 ↔ 방문 목적 / 내용 ↔ 작업 내용 / 담당자 ↔ 방문자 / 시작 ↔ 방문일
 */
export interface RecordCarry {
  /** 현장 선택 모드에서만 의미 있다. */
  siteId: string;
  title: string;
  body: string;
  people: string[];
  /** datetime-local 형식 문자열. */
  datetimeLocal: string;
  /**
   * 위 날짜가 폼이 편의상 채워 넣은 값인가(사용자가 직접 고른 게 아닌가).
   *
   * 메모는 날짜가 선택인데, 자동으로 채워진 "지금" 이 따라붙으면 날짜 없는
   * 메모를 쓰려던 의도와 반대로 캘린더에 올라간다. 그래서 메모로 옮길 때는
   * 자동 채움 값만 버린다.
   */
  datetimeAuto: boolean;
}

export const EMPTY_CARRY: RecordCarry = {
  siteId: '',
  title: '',
  body: '',
  people: [],
  datetimeLocal: '',
  datetimeAuto: false,
};

const RecordKindSelector: React.FC<RecordKindSelectorProps> = ({
  value,
  onChange,
  kinds = RECORD_KINDS,
  disabled,
}) => (
  <Box
    role="radiogroup"
    aria-label="기록 종류"
    sx={{
      display: 'grid',
      // 좁은 화면 2×2, 그 위는 한 줄. 4열을 xs 에 밀어넣으면 라벨이 잘린다.
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${kinds.length}, 1fr)` },
      gap: 1,
    }}
  >
    {kinds.map((k) => {
      const meta = RECORD_KIND_META[k];
      const selected = value === k;
      return (
        <ButtonBase
          key={k}
          role="radio"
          aria-checked={selected}
          aria-label={meta.label}
          disabled={disabled}
          onClick={() => onChange(k)}
          sx={{
            flexDirection: 'column',
            gap: 0.25,
            py: 1.25,
            px: 1,
            minHeight: 64,
            borderRadius: 1.5,
            border: '2px solid',
            borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'action.selected' : 'transparent',
            color: selected ? 'primary.main' : 'text.secondary',
            transition: 'border-color .12s, background-color .12s',
            '&:hover': { borderColor: selected ? 'primary.main' : 'text.disabled' },
            '&.Mui-disabled': { opacity: 0.5 },
          }}
        >
          <meta.Icon fontSize="small" />
          <Typography variant="body2" fontWeight={selected ? 800 : 600} lineHeight={1.2}>
            {meta.label}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.8, lineHeight: 1.2 }}>
            {meta.caption}
          </Typography>
        </ButtonBase>
      );
    })}
  </Box>
);

export default RecordKindSelector;

/** 선택된 종류의 안내 문구를 폼 상단에 한 줄로. */
export const RecordKindHint: React.FC<{ kind: RecordKind }> = ({ kind }) => (
  <Stack direction="row" sx={{ mt: 0.75 }}>
    <Typography variant="caption" color="text.secondary">
      {RECORD_KIND_META[kind].hint}
    </Typography>
  </Stack>
);
